import React, { useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { comparison, vrfbFits, vrfbSteps } from '../../data/technology';
import { EnergyTrace } from '../ui/EnergyTrace';
import { Reveal } from '../ui/Reveal';
import { SectionHeader } from '../ui/SectionHeader';
export function Technology() {
  const [explainerOpen, setExplainerOpen] = useState(false);
  return <section id="technology" aria-labelledby="technology-title" className="bg-white" data-id="element-508" data-mp="auz3xx1a70y1">
      <EnergyTrace variant="violet" data-id="element-509" data-mp="axbkq6vaz59i" />
      <div className="mx-auto max-w-page px-5 py-20 lg:px-8 lg:py-28" data-id="element-510" data-mp="fubq0fkbw3zk">
        <Reveal data-id="element-511" data-mp="22neoehwljjn">
          <SectionHeader id="technology-title" title="Flow batteries, explained plainly" intro="Long-duration storage built on vanadium redox flow battery (VRFB) technology, and honest advice on when it’s the right choice." data-id="element-512" data-mp="2370i6aocs2y" />
        </Reveal>

        <div className="mt-14 grid gap-12 lg:grid-cols-2 lg:gap-16" data-id="element-513" data-mp="ykbd4wwlz90c">
          <Reveal data-id="element-514" data-mp="ymo4g0o9avq2">
            <h3 className="font-sora text-xl font-semibold" data-id="element-515" data-mp="3qrukb996dahh">What is a VRFB?</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-afb-ink/80" data-id="element-516" data-mp="3qprw8url1rfe">
              A vanadium redox flow battery stores energy in liquid electrolyte held in external tanks. Pumps move it
              through a cell stack to charge and discharge. Energy (tank volume) and power (stack size) are separate,
              so each can be sized to the job.
            </p>
            <button type="button" onClick={() => setExplainerOpen(open => !open)} aria-expanded={explainerOpen} aria-controls="vrfb-explainer" className="mt-6 inline-flex items-center gap-2 rounded-btn border border-afb-ink/15 px-4 py-2.5 text-sm font-semibold text-afb-ink transition-colors duration-150 hover:border-afb-ink/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-afb-turquoise" data-id="element-517" data-mp="dbddkayfvm79">
              Explore technology
              <ChevronDownIcon aria-hidden="true" className={`h-4 w-4 transition-transform duration-200 ${explainerOpen ? 'rotate-180' : ''}`} data-id="element-518" data-mp="2k5qwwed5xdhz" />
            </button>
            <div id="vrfb-explainer" className="collapse-grid" data-open={explainerOpen} data-id="element-519" data-mp="x0xw30qx5v0e">
              <div className="overflow-hidden" data-id="element-520" data-mp="1200zkgr93t69">
                <ol className="mt-6 grid gap-4 sm:grid-cols-3" data-id="element-521" data-mp="120txqtc59acs">
                  {vrfbSteps.map((step, index) => <li key={step.title} className="rounded-card bg-afb-light p-5" data-id="element-522" data-mp="16yvg1c1qx5n7" data-mpi={index}>
                      <p className="font-sora text-sm font-semibold text-afb-teal" data-id="element-523" data-mp="1bwx8gnl2ahbc">
                        {index + 1}. {step.title}
                      </p>
                      <p className="mt-2 text-sm leading-relaxed text-afb-ink/80" data-id="element-524" data-mp="1kqabiicuazb5">{step.detail}</p>
                    </li>)}
                </ol>
              </div>
            </div>
          </Reveal>

          <Reveal delay={60} data-id="element-525" data-mp="1unwvvkcwrj2i">
            <h3 className="font-sora text-xl font-semibold" data-id="element-526" data-mp="1up801la9msu5">Where it fits</h3>
            <dl className="mt-3 divide-y divide-afb-line" data-id="element-527" data-mp="24to8vupbv9og">
              {vrfbFits.map(fit => <div key={fit.title} className="py-4" data-id="element-528" data-mp="1zsikua6m4al9">
                  <dt className="font-semibold text-afb-ink" data-id="element-529" data-mp="1uuh1do59y999">{fit.title}</dt>
                  <dd className="mt-1 text-[15px] leading-relaxed text-afb-muted" data-id="element-530" data-mp="1uwjo9zb4rm9r">{fit.detail}</dd>
                </div>)}
            </dl>
          </Reveal>
        </div>

        <Reveal className="mt-16" data-id="element-531" data-mp="2nimh1whh1828">
          <h3 className="font-sora text-xl font-semibold" data-id="element-532" data-mp="2ik1gtx27yods">Choose the right tech</h3>
          <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-afb-muted" data-id="element-533" data-mp="2imdy0805kups">
            No single technology wins everywhere. Lithium-ion suits many short-duration or space-limited jobs, and
            diesel still has a role as on-demand backup. We’ll recommend what fits your site.
          </p>
          <div className="mt-6 overflow-x-auto rounded-card border border-afb-line" data-id="element-534" data-mp="36bgn20x49pub">
            <table className="w-full min-w-[640px] text-left text-[15px]" data-id="element-535" data-mp="31gszc49xszgs">
              <caption className="sr-only" data-id="element-536" data-mp="2lzb6nqxfpryx">General comparison of storage and generation technologies</caption>
              <thead className="bg-afb-light" data-id="element-537" data-mp="2r1abz6cqiyyi">
                <tr data-id="element-538" data-mp="2d0pqg2ffqp1b">
                  <th scope="col" className="w-[22%] px-5 py-4 text-sm font-medium text-afb-muted" data-id="element-539" data-mp="2d1995lcwthge">
                    <span className="sr-only" data-id="element-540" data-mp="2h2ul94hmdrpj">Characteristic</span>
                  </th>
                  <th scope="col" className="px-5 py-4 font-sora text-sm font-semibold text-afb-ink" data-id="element-541" data-mp="34r41zrzz7p9t">
                    Flow battery (VRFB)
                  </th>
                  <th scope="col" className="px-5 py-4 font-sora text-sm font-semibold text-afb-ink" data-id="element-542" data-mp="2vz1zlo6cjyww">
                    Lithium-ion
                  </th>
                  <th scope="col" className="px-5 py-4 font-sora text-sm font-semibold text-afb-ink" data-id="element-543" data-mp="2zz44053gv6fv">
                    Diesel generator
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-afb-line" data-id="element-544" data-mp="3sij4plvo08ll">
                {comparison.map(row => <tr key={row.label} data-id="element-545" data-mp="1h068fmpj6cr2">
                    <th scope="row" className="px-5 py-4 align-top text-sm font-medium text-afb-muted" data-id="element-546" data-mp="1c3601557kxws">
                      {row.label}
                    </th>
                    <td className="px-5 py-4 align-top text-afb-ink" data-id="element-547" data-mp="1qr0sqlj7v29v">{row.vrfb}</td>
                    <td className="px-5 py-4 align-top text-afb-ink/80" data-id="element-548" data-mp="1lt7qu4oy4ufe">{row.lithium}</td>
                    <td className="px-5 py-4 align-top text-afb-ink/80" data-id="element-549" data-mp="y3wkm7otu7tv">{row.diesel}</td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-afb-muted" data-id="element-550" data-mp="181kpvrk26xqa">
            General technology characteristics, not AFB product specifications.
          </p>
        </Reveal>
      </div>
    </section>;
}